"use client";
import React, { useState, useRef, useEffect } from "react";
import { useField, useFormikContext } from "formik";
import { BiChevronDown } from "react-icons/bi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

type Options = {
  value: string;
  label: string;
  code?: string;
  _id?: string;
};

interface Props {
  options: Options[];
  label: string;
  name: string;
  id: string;
  required?: boolean;
  onChange?: (e: any) => void;
  isLoading?: boolean;
  disabled?: boolean;
  onSearch?: (value: string) => void;
  searchValue?: string;
  disabledMessage?: string;
}

const SearchableInput: React.FC<Props> = ({
  label,
  name,
  id,
  options,
  required,
  onChange,
  isLoading = false,
  disabled = false,
  onSearch,
  searchValue = "",
  disabledMessage,
  ...res
}) => {
  const [search, setSearch] = useState("");
  const [dropdown, setDropdown] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext<unknown>();

  // Sync internal search with parent search value
  useEffect(() => {
    setSearch(searchValue);
  }, [searchValue]);

  const selectedOption = options.find(
    (opt) => opt.value.toLowerCase() === (field.value || "").toLowerCase()
  );

  const displayValue = dropdown ? search : selectedOption?.label || "";

  const filteredOptions = (dropdown ? search : "")
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const handleSearch = (value: string) => {
    setSearch(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSelect = (opt: Options) => {
    setFieldValue(name, opt.value);
    setSearch("");
    setDropdown(false);
    // Call onChange callback if provided
    if (onChange) {
      onChange({ target: { value: opt.value } });
    }
  };

  return (
    <div
      className="flex flex-col relative w-full"
      ref={containerRef}
      title={disabledMessage}
    >
      <label className="font-medium text-base mt-3" htmlFor={id || name}>
        {label} {required && <span className="text-red-500 text-lg">*</span>}
      </label>

      <div className="relative inline w-full  p-0 m-0">
        <div className="relative flex items-center">
          {selectedOption?.code && !dropdown && (
            <img
              src={`https://flagcdn.com/w20/${selectedOption.code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${selectedOption.code.toLowerCase()}.png 2x`}
              width="20"
              alt={`${selectedOption.value} flag`}
              className="absolute left-2 pointer-events-none"
            />
          )}
          {isLoading && (
            <AiOutlineLoading3Quarters
              size={18}
              className="absolute left-2 pointer-events-none animate-spin text-orange-400"
            />
          )}
          <input
            className={` border w-full rounded-lg border-[#E2E5EA] py-2 ${
              selectedOption?.code && !dropdown ? "pl-8" : "pl-2"
            } pr-8 focus:border-orange-400 focus:outline-orange-400 border-solid disabled:bg-gray-100 disabled:cursor-not-allowed`}
            type="text"
            name={name}
            id={id || name}
            value={displayValue}
            onChange={(e) => {
              handleSearch(e.target.value);
              setDropdown(true);
            }}
            onFocus={() => !disabled && setDropdown(true)}
            onBlur={(e) => {
              field.onBlur(e);
              setDropdown(false);
            }}
            disabled={disabled}
            {...res}
          />
        </div>

        <BiChevronDown
          size={18}
          strokeWidth={1}
          className={`absolute right-2 top-[28%] ${
            disabled
              ? "text-gray-300 cursor-not-allowed"
              : "text-gray cursor-pointer"
          }`}
          onClick={() => !disabled && setDropdown((prev) => !prev)}
        />

        {dropdown && !disabled && (
          <div className="absolute top-[105%] py-3 max-h-56 overflow-auto bg-white w-full z-50 border shadow-md">
            {isLoading ? (
              <div className="flex items-center justify-center px-3 py-2 text-gray-500 text-sm gap-2">
                <AiOutlineLoading3Quarters size={16} className="animate-spin" />
                Loading...
              </div>
            ) : filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((opt) => (
                  <div
                    key={opt._id || opt.value}
                    onMouseDown={() => handleSelect(opt)}
                    className="flex items-center gap-2 w-full px-3 py-2 hover:bg-primary hover:text-white cursor-pointer text-sm"
                  >
                    {opt.code && (
                      <img
                        src={`https://flagcdn.com/w20/${opt.code.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${opt.code.toLowerCase()}.png 2x`}
                        width="20"
                        alt={`${opt.value} flag`}
                        className="inline-block"
                      />
                    )}
                    <span>{opt.label}</span>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">No results</div>
            )}
          </div>
        )}

        {disabled && disabledMessage && (
          <div className="text-sm text-gray-500 mt-1">{disabledMessage}</div>
        )}

        {meta.error && meta.touched && (
          <div className="absolute text-red-500 -z-10">{meta.error}</div>
        )}
      </div>
    </div>
  );
};

export default SearchableInput;
